import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Send } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/auth";
import { REVIEWS as SEED } from "@/lib/data";

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", stars: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const merged = [...reviews, ...SEED];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.text) {
      toast.error("Please fill every field.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/reviews`, form);
      toast.success(r.data.message || "Thank you.");
      setSubmitted(true);
      setForm({ name: "", email: "", stars: 5, text: "" });
    } catch {
      toast.error("Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avg =
    merged.length > 0
      ? (merged.reduce((s, r) => s + (r.stars || 5), 0) / merged.length).toFixed(1)
      : "5.0";

  return (
    <div data-testid="page-reviews" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-8 lg:grid-cols-12 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="overline">Reviews</div>
            <h1 className="font-display text-5xl sm:text-7xl mt-4 tracking-tight text-balance">
              Loved by <span className="italic font-light">{Math.max(merged.length * 25, 500)}+ women.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[hsl(var(--onyx)/0.65)]">
              Real words from real clients — Google, Facebook and directly from the salon.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <div className="inline-flex items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className="text-[hsl(var(--gold))]" fill="currentColor" strokeWidth={0} />
              ))}
              <span className="font-display text-2xl ml-1">{avg}</span>
            </div>
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)] mt-1">
              {merged.length} reviews
            </div>
          </div>
        </div>

        <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {merged.map((r, i) => (
            <Item key={r.id || i}>
              <div
                data-testid={`review-card-${i}`}
                className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-7 h-full flex flex-col"
              >
                <div className="flex items-center gap-1 text-[hsl(var(--gold))]">
                  {[...Array(r.stars || 5)].map((_, k) => (
                    <Star key={k} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="font-display text-xl mt-4 leading-snug text-[hsl(var(--onyx))]">
                  “{r.text}”
                </p>
                <div className="mt-6 pt-6 border-t border-[hsl(var(--onyx)/0.06)] flex items-center justify-between text-sm">
                  <span className="font-medium">{r.name}</span>
                  <span className="text-[hsl(var(--onyx)/0.5)]">{r.source || "Website"}</span>
                </div>
              </div>
            </Item>
          ))}
        </Stagger>

        <Reveal className="mt-24 rounded-[2rem] bg-blush-soft p-10 sm:p-14">
          <div className="grid gap-10 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <div className="overline">Share your experience</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight text-balance">
                Loved your visit? <span className="italic font-light">Let us know.</span>
              </h2>
              <p className="mt-4 text-[hsl(var(--onyx)/0.65)] max-w-md leading-relaxed">
                Your words help other women find us. Every review is read personally — thank you.
              </p>
            </div>
            <div className="lg:col-span-7">
              {submitted ? (
                <div className="rounded-3xl bg-white p-10 text-center">
                  <Star size={22} className="text-[hsl(var(--gold))] mx-auto" fill="currentColor" strokeWidth={0} />
                  <div className="overline mt-4">Thank you</div>
                  <h3 className="font-display text-3xl mt-3">
                    Your review has been submitted.
                  </h3>
                  <p className="mt-4 text-sm text-[hsl(var(--onyx)/0.6)] max-w-sm mx-auto">
                    It'll appear here once approved. We really appreciate you.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="review-form"
                  className="rounded-3xl bg-white p-8 grid gap-5"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="r-name">Name</Label>
                      <Input
                        id="r-name"
                        data-testid="review-name"
                        className="mt-2 h-12 rounded-2xl"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="r-email">Email</Label>
                      <Input
                        id="r-email"
                        type="email"
                        data-testid="review-email"
                        className="mt-2 h-12 rounded-2xl"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Rating</Label>
                    <div className="mt-2 flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setForm({ ...form, stars: s })}
                          data-testid={`review-star-${s}`}
                          className="p-1 transition-transform duration-200 hover:scale-110"
                          aria-label={`${s} stars`}
                        >
                          <Star
                            size={26}
                            className={s <= form.stars ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--onyx)/0.15)]"}
                            fill={s <= form.stars ? "currentColor" : "none"}
                            strokeWidth={s <= form.stars ? 0 : 1.5}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="r-text">Your review</Label>
                    <Textarea
                      id="r-text"
                      rows={5}
                      data-testid="review-text"
                      className="mt-2 rounded-2xl"
                      value={form.text}
                      onChange={(e) => setForm({ ...form, text: e.target.value })}
                      placeholder="Tell us about your experience…"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="review-submit"
                    className="btn-pill btn-primary self-start disabled:opacity-60"
                  >
                    {submitting ? "Submitting…" : "Submit review"} <Send size={14} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
